import * as i0 from '@angular/core';
import { ChangeDetectorRef, Component, ChangeDetectionStrategy, Optional, Self, Inject, ViewChild, Input, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { NgControl, FormsModule } from '@angular/forms';
import { AbstractTuiNullableControl, TUI_DEFAULT_IDENTITY_MATCHER, tuiAsFocusableItemAccessor, tuiAsControl, TuiFocusableModule, TuiFocusedModule } from '@taiga-ui/cdk';
import { TUI_MODE, MODE_PROVIDER } from '@taiga-ui/core';
import * as i1 from '@taiga-ui/kit/components/radio';
import { TUI_RADIO_OPTIONS, TuiRadioComponent, TuiRadioModule } from '@taiga-ui/kit/components/radio';
import * as i3 from 'rxjs';
import { CommonModule } from '@angular/common';

class TuiRadioLabeledComponent extends AbstractTuiNullableControl {
    constructor(control, cdr, mode$, options) {
        super(control, cdr);
        this.mode$ = mode$;
        this.options = options;
        this.size = this.options.size;
        this.identityMatcher = TUI_DEFAULT_IDENTITY_MATCHER;
        this.pseudoDisabled = false;
    }
    get nativeFocusableElement() {
        return this.radio ? this.radio.nativeFocusableElement : null;
    }
    get focused() {
        return !!this.radio && this.radio.focused;
    }
    get computedDisabled() {
        return this.disabled || this.pseudoDisabled;
    }
    onFocused(focused) {
        this.updateFocused(focused);
    }
    /** @deprecated use 'value' setter */
    onModelChange(value) {
        this.value = value;
    }
}
TuiRadioLabeledComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiRadioLabeledComponent, deps: [{ token: NgControl, optional: true, self: true }, { token: ChangeDetectorRef }, { token: TUI_MODE }, { token: TUI_RADIO_OPTIONS }], target: i0.ɵɵFactoryTarget.Component });
TuiRadioLabeledComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: TuiRadioLabeledComponent, selector: "tui-radio-labeled", inputs: { item: "item", size: "size", identityMatcher: "identityMatcher", pseudoDisabled: "pseudoDisabled" }, host: { listeners: { "$.data-mode.attr": "mode$" }, properties: { "attr.data-size": "size" } }, providers: [
        tuiAsFocusableItemAccessor(TuiRadioLabeledComponent),
        tuiAsControl(TuiRadioLabeledComponent),
        MODE_PROVIDER,
    ], viewQueries: [{ propertyName: "radio", first: true, predicate: TuiRadioComponent, descendants: true }], usesInheritance: true, ngImport: i0, template: "<label class=\"t-wrapper\">\n    <tui-radio\n        [focusable]=\"focusable\"\n        [identityMatcher]=\"identityMatcher\"\n        [name]=\"computedName || ''\"\n        [nativeId]=\"nativeId\"\n        [readOnly]=\"readOnly\"\n        [pseudoDisabled]=\"computedDisabled\"\n        [pseudoInvalid]=\"computedInvalid\"\n        [pseudoFocus]=\"pseudoFocus\"\n        [pseudoHover]=\"pseudoHover\"\n        [pseudoActive]=\"pseudoActive\"\n        [size]=\"size\"\n        [item]=\"item\"\n        [(ngModel)]=\"value\"\n        (focusedChange)=\"onFocused($event)\"\n    ></tui-radio>\n    <div\n        class=\"t-content\"\n        [class.t-content_disabled]=\"computedDisabled\"\n    >\n        <ng-content></ng-content>\n    </div>\n</label>\n", styles: [":host{position:relative;display:block;font:var(--tui-font-text-s);line-height:1rem}:host._readonly{pointer-events:none}.t-wrapper{display:inline-flex;max-width:100%;cursor:pointer;vertical-align:top}:host._disabled .t-wrapper{cursor:default}:host[data-mode=onDark] .t-wrapper{color:var(--tui-text-01-night)}.t-content{word-wrap:break-word;min-width:0}.t-content_disabled{opacity:var(--tui-disabled-opacity)}:host[data-size=m] .t-content{margin-left:.5rem}:host[data-size=l] .t-content{font:var(--tui-font-text-m);margin-left:.75rem}\n"], components: [{ type: i1.TuiRadioComponent, selector: "tui-radio", inputs: ["item", "identityMatcher", "name", "size", "pseudoDisabled"] }], directives: [{ type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { type: i2.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiRadioLabeledComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'tui-radio-labeled',
                    templateUrl: './radio-labeled.template.html',
                    styleUrls: ['./radio-labeled.style.less'],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    providers: [
                        tuiAsFocusableItemAccessor(TuiRadioLabeledComponent),
                        tuiAsControl(TuiRadioLabeledComponent),
                        MODE_PROVIDER,
                    ],
                    host: {
                        '($.data-mode.attr)': 'mode$',
                        '[attr.data-size]': 'size',
                    },
                }]
        }], ctorParameters: function () { return [{ type: i2.NgControl, decorators: [{
                    type: Optional
                }, {
                    type: Self
                }, {
                    type: Inject,
                    args: [NgControl]
                }] }, { type: i0.ChangeDetectorRef, decorators: [{
                    type: Inject,
                    args: [ChangeDetectorRef]
                }] }, { type: i3.Observable, decorators: [{
                    type: Inject,
                    args: [TUI_MODE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [TUI_RADIO_OPTIONS]
                }] }]; }, propDecorators: { radio: [{
                type: ViewChild,
                args: [TuiRadioComponent]
            }], item: [{
                type: Input
            }], size: [{
                type: Input
            }], identityMatcher: [{
                type: Input
            }], pseudoDisabled: [{
                type: Input
            }] } });

class TuiRadioLabeledModule {
}
TuiRadioLabeledModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiRadioLabeledModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TuiRadioLabeledModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiRadioLabeledModule, declarations: [TuiRadioLabeledComponent], imports: [CommonModule,
        FormsModule,
        TuiFocusableModule,
        TuiFocusedModule,
        TuiRadioModule], exports: [TuiRadioLabeledComponent] });
TuiRadioLabeledModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiRadioLabeledModule, imports: [[
            CommonModule,
            FormsModule,
            TuiFocusableModule,
            TuiFocusedModule,
            TuiRadioModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: TuiRadioLabeledModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        TuiFocusableModule,
                        TuiFocusedModule,
                        TuiRadioModule,
                    ],
                    declarations: [TuiRadioLabeledComponent],
                    exports: [TuiRadioLabeledComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TuiRadioLabeledComponent, TuiRadioLabeledModule };
